// (c) chaehongee. Confidential and proprietary.

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight } from 'lucide-react'

import type { UserResponse } from 'models-ts/user'

import { getUserProfile } from '../../../services/user'
import {
    cancelCreditPurchase,
    cancelSubscription,
    getBillingConfig,
    getSubscriptionStatus,
    listCreditPurchases,
    formatCurrencyAmount,
    type BillingConfig,
    type CreditPurchaseItem,
    type SubscriptionStatusResponse,
} from '../../../services/billing'
import { CreditPurchaseForm } from '../../shared/CreditPurchaseForm'
import styles from './PageLayout.module.css'
import s from './BillingInvoices.module.css'

const TIER_LABELS: Record<string, string> = {
    explorer: 'tierExplorer',
    pro: 'tierPro',
    studio: 'tierStudio',
}

function formatPeriodEnd(dateStr: string | null, locale: string): string {
    if (!dateStr) return '\u2014'
    return new Date(dateStr).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    })
}

// ── Credit Balance Hero ──────────────────────────────────

function CreditBalanceHero({
    balance,
    subscription,
    isLoading,
}: {
    balance: number | null
    subscription: SubscriptionStatusResponse | null
    isLoading: boolean
}) {
    const { t, i18n } = useTranslation('billing')

    if (isLoading) return null

    const balanceLabel =
        typeof balance === 'number'
            ? new Intl.NumberFormat().format(balance)
            : '\u2014'

    const hasAllocation =
        subscription && subscription.monthly_credit_allocation > 0
    const used = hasAllocation
        ? Math.max(0, subscription.monthly_credit_allocation - (balance ?? 0))
        : 0
    const pct = hasAllocation
        ? Math.min(100, Math.round((used / subscription.monthly_credit_allocation) * 100))
        : 0
    const periodEnd = subscription
        ? formatPeriodEnd(subscription.current_period_end, i18n.language)
        : null

    return (
        <div className={s.balanceHero}>
            <p className={s.balanceLabel}>{t('creditBalance')}</p>
            <p className={s.balanceNumber}>
                {balanceLabel}
                <span className={s.balanceUnit}>cr</span>
            </p>

            {hasAllocation ? (
                <>
                    <p className={s.balanceSub}>
                        {t('creditsPerMonth', {
                            amount: new Intl.NumberFormat().format(
                                subscription.monthly_credit_allocation,
                            ),
                        })}
                        {periodEnd && !subscription.cancel_at_period_end &&
                            <> &middot; {t('renewsOn', { date: periodEnd })}</>
                        }
                    </p>
                    <div className={s.balanceBar}>
                        <div className={s.barTrack}>
                            <div className={s.barFill} style={{ width: `${pct}%` }} />
                        </div>
                        <div className={s.barLabels}>
                            <span>
                                {new Intl.NumberFormat().format(used)} {t('used')}
                            </span>
                            <span>
                                {new Intl.NumberFormat().format(subscription.monthly_credit_allocation)}
                            </span>
                        </div>
                    </div>
                </>
            ) : (
                <p className={s.balanceSub}>{t('creditBalanceDescription')}</p>
            )}
        </div>
    )
}

// ── Plan Card (Subscribed) ───────────────────────────────

function SubscriptionPlanCard({
    subscription,
    onCanceled,
}: {
    subscription: SubscriptionStatusResponse
    onCanceled: (updated: SubscriptionStatusResponse) => void
}) {
    const { t, i18n } = useTranslation('billing')
    const [showCancelConfirm, setShowCancelConfirm] = useState(false)
    const [isCanceling, setIsCanceling] = useState(false)
    const [cancelError, setCancelError] = useState<string | null>(null)

    const tierKey = TIER_LABELS[subscription.tier] ?? subscription.tier
    const tierName = t(tierKey)
    const intervalLabel = subscription.interval === 'annual' ? t('annual') : t('monthly')
    const periodEnd = formatPeriodEnd(subscription.current_period_end, i18n.language)

    const handleCancel = async () => {
        setIsCanceling(true)
        setCancelError(null)
        try {
            const updated = await cancelSubscription(false)
            onCanceled(updated)
            setShowCancelConfirm(false)
        } catch {
            setCancelError(t('cancelFailed'))
        } finally {
            setIsCanceling(false)
        }
    }

    return (
        <div className={`${styles.section} ${s.planCard}`}>
            <div className={s.planRow}>
                <div className={s.planLeft}>
                    <span className={s.tierBadge}>
                        <span className={s.tierDot} />
                        {tierName}
                    </span>
                    <span className={s.intervalTag}>{intervalLabel}</span>
                    <span className={s.planMeta}>
                        {subscription.cancel_at_period_end
                            ? t('cancelsOn', { date: periodEnd })
                            : t('renewsOn', { date: periodEnd })
                        }
                    </span>
                </div>

                <div className={s.planLinks}>
                    <button
                        className={s.planLink}
                        onClick={() => (window.location.href = '/pricing')}
                    >
                        {t('changePlan')}
                    </button>
                    {!subscription.cancel_at_period_end && (
                        <button
                            className={s.planLinkDanger}
                            onClick={() => setShowCancelConfirm(true)}
                        >
                            {t('cancelSubscription')}
                        </button>
                    )}
                </div>
            </div>

            {subscription.cancel_at_period_end && (
                <div className={s.cancelBanner}>{t('cancelingNotice')}</div>
            )}

            {showCancelConfirm && (
                <div className={s.cancelConfirm}>
                    <p className={s.cancelConfirmTitle}>{t('cancelConfirmTitle')}</p>
                    <p className={s.cancelConfirmMessage}>{t('cancelConfirmMessage')}</p>
                    <div className={s.cancelConfirmActions}>
                        <button
                            className={s.dangerButton}
                            onClick={() => void handleCancel()}
                            disabled={isCanceling}
                        >
                            {isCanceling ? t('canceling') : t('cancelConfirm')}
                        </button>
                        <button
                            className={s.secondaryButton}
                            onClick={() => setShowCancelConfirm(false)}
                            disabled={isCanceling}
                        >
                            {t('cancelDismiss')}
                        </button>
                    </div>
                    {cancelError && <div className={s.error}>{cancelError}</div>}
                </div>
            )}
        </div>
    )
}

// ── Free User Upgrade Card ───────────────────────────────

function FreeUpgradeCard() {
    const { t } = useTranslation('billing')

    return (
        <div className={`${styles.section} ${s.upgradeCard}`}>
            <h3 className={s.upgradeTitle}>{t('noPlan')}</h3>
            <p className={s.upgradeDesc}>{t('noPlanMessage')}</p>
            <button
                className={s.upgradeCta}
                onClick={() => (window.location.href = '/pricing')}
            >
                {t('viewPlans')}
                <ChevronRight size={14} />
            </button>
        </div>
    )
}

// ── Buy Credits (Expandable) ─────────────────────────────

function BuyCreditsSection({
    billingConfig,
    onCreditsAdded,
}: {
    billingConfig: BillingConfig | null
    onCreditsAdded: (credits: number, balance: number) => void
}) {
    const { t } = useTranslation('billing')
    const [isOpen, setIsOpen] = useState(false)

    return (
        <div className={s.buySection}>
            <button
                className={s.buyToggle}
                onClick={() => setIsOpen(!isOpen)}
            >
                {t('buyCredits')}
                <ChevronDown
                    size={14}
                    className={s.buyToggleIcon}
                    data-open={isOpen}
                />
            </button>

            {isOpen && billingConfig && (
                <div className={s.buyExpanded}>
                    <CreditPurchaseForm
                        billingConfig={billingConfig}
                        onCreditsAdded={onCreditsAdded}
                    />
                </div>
            )}
        </div>
    )
}

// ── Purchase History ─────────────────────────────────────

function PurchaseHistory({ purchases }: { purchases: CreditPurchaseItem[] }) {
    const { t } = useTranslation('billing')

    if (purchases.length === 0) return null

    return (
        <div className={s.historySection}>
            <div className={s.historyHeader}>
                <h3 className={s.historyTitle}>{t('purchaseHistory')}</h3>
            </div>
            <table className={s.table}>
                <thead>
                    <tr>
                        <th className={s.th}>{t('date')}</th>
                        <th className={s.th}>{t('amount')}</th>
                        <th className={s.th}>{t('credits')}</th>
                        <th className={s.th}>{t('status')}</th>
                    </tr>
                </thead>
                <tbody>
                    {purchases.map((p) => (
                        <tr key={p.id}>
                            <td className={s.td}>
                                {new Date(p.purchased_at).toLocaleDateString()}
                            </td>
                            <td className={s.td}>
                                <span className={s.mono}>
                                    {formatCurrencyAmount(
                                        p.currency || 'usd',
                                        p.amount ?? p.dollars,
                                    )}
                                </span>
                            </td>
                            <td className={s.td}>
                                <span className={s.mono}>
                                    {new Intl.NumberFormat().format(p.credits_added)}
                                </span>
                            </td>
                            <td className={s.td}>
                                <span className={s.statusBadge} data-status={p.status}>
                                    {p.status}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

// ── Main Billing Page ─────────────────────────────────────

export function BillingInvoices() {
    const { t } = useTranslation('billing')
    const [profile, setProfile] = useState<UserResponse | null>(null)
    const [purchases, setPurchases] = useState<CreditPurchaseItem[]>([])
    const [subscription, setSubscription] = useState<SubscriptionStatusResponse | null>(null)
    const [subscriptionLoadFailed, setSubscriptionLoadFailed] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [billingConfig, setBillingConfig] = useState<BillingConfig | null>(null)

    useEffect(() => {
        if (!error) return
        const timeoutId = window.setTimeout(() => setError(null), 10_000)
        return () => window.clearTimeout(timeoutId)
    }, [error])

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const checkout = params.get('checkout')
        const purchaseId = params.get('purchase_id')

        if (checkout === 'cancel' && purchaseId) {
            void (async () => {
                try {
                    const updated = await cancelCreditPurchase(purchaseId)
                    setPurchases((prev) => {
                        const idx = prev.findIndex((p) => p.id === updated.id)
                        if (idx === -1) return [updated, ...prev]
                        return prev.map((p) => (p.id === updated.id ? updated : p))
                    })
                    setError(t('purchaseCanceled'))
                } catch {
                    setError(t('purchaseCancelFailed'))
                } finally {
                    const url = new URL(window.location.href)
                    url.searchParams.delete('checkout')
                    url.searchParams.delete('purchase_id')
                    window.history.replaceState({}, '', url.toString())
                }
            })()
        }
        void load()
    }, [])

    const load = async () => {
        try {
            setIsLoading(true)
            setError(null)
            const [nextProfile, history, config, subResult] = await Promise.all([
                getUserProfile(),
                listCreditPurchases(50),
                getBillingConfig(),
                // 404 means subscriptions are disabled — treat as "no plan".
                // Any other failure must NOT be shown as "no plan": a paying
                // subscriber would lose the cancel button with no explanation.
                getSubscriptionStatus().then(
                    (status) => ({ failed: false as const, status }),
                    (err: unknown) => ({
                        failed: (err as { status?: number })?.status !== 404,
                        status: null,
                    }),
                ),
            ])
            setProfile(nextProfile)
            setPurchases(history.purchases)
            setBillingConfig(config)
            setSubscriptionLoadFailed(subResult.failed)
            setSubscription(
                subResult.status && subResult.status.status !== 'none'
                    ? subResult.status
                    : null,
            )
        } catch {
            setError(t('loadFailed'))
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className={styles.page}>
            {/* 1. Credit Balance Hero */}
            <CreditBalanceHero
                balance={profile?.credit_balance ?? null}
                subscription={subscription}
                isLoading={isLoading}
            />

            {/* 2. Plan Card */}
            {!isLoading && (
                subscriptionLoadFailed ? (
                    <div className={`${styles.section} ${s.planCard}`}>
                        <div className={s.error}>{t('subscriptionLoadFailed')}</div>
                    </div>
                ) : subscription ? (
                    <SubscriptionPlanCard
                        subscription={subscription}
                        onCanceled={setSubscription}
                    />
                ) : (
                    <FreeUpgradeCard />
                )
            )}

            {/* 3. Buy Credits */}
            <BuyCreditsSection
                billingConfig={billingConfig}
                onCreditsAdded={(_credits, balance) => {
                    setProfile((prev) =>
                        prev ? { ...prev, credit_balance: balance } : prev,
                    )
                }}
            />

            {/* 4. Purchase History */}
            <PurchaseHistory purchases={purchases} />

            {error && <div className={s.error}>{error}</div>}
        </div>
    )
}
